import {
  BitkitAlert,
  BitkitButton,
  BitkitEmptyState,
  BitkitLink,
  BitkitLinkButton,
  IconArrowNortheast,
  IconBuildCache,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Text } from '@chakra-ui/react/text';
import { useContext, useEffect } from 'react';

import { useBuildCacheAddonTrial } from '@/client/BFF/Organizations/AddonTrials/useBuildCacheAddonTrial';
import BuildInvocations from '@/components/BuildCache/components/BuildInvocations';
import OptimizableGradleSteps from '@/components/BuildCache/components/OptimizableGradleSteps';
import SetUpBuildCache from '@/components/BuildCache/components/SetUpBuildCache';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { BuildDetailsTabId } from '@/enums/BuildDetailsTabs';
import { segmentTrack, segmentTrackOnce } from '@/hooks/analytics/useTracking';
import useQueryParams from '@/hooks/useQueryParams';
import { isBuildNotFinished } from '@/models/Build';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import BuildStatus from '@/models/BuildStatus';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import ActivationAlert from '@/pages/BuildCache/components/BuildCacheActivation/ActivationAlert';
import ActivationCard from '@/pages/BuildCache/components/BuildCacheActivation/ActivationCard';
import RequestSupportDialog from '@/pages/BuildCache/components/BuildCacheActivation/RequestSupportDialog';
import { BuildCacheActivationSourcePageType } from '@/pages/BuildCache/components/BuildCacheActivation/types/BuildCacheActivation';
import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { buildCacheConnectionPath } from '@/utils/monolithApiRouteService';

const BuildCacheOfBuild = () => {
  const {
    account: {
      slug: accountSlug,
      canActivateBuildCacheTrial,
      canViewWorkspace,
      hasBuildCacheSubscription,
      buildCacheSubscriptionRequest,
    },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { trial: buildCacheTrial, isLoading: isBuildCacheTrialLoading } = useBuildCacheAddonTrial(accountSlug);

  const app = useBuildDetailsPageStore((state) => state.app);
  const build = useBuildDetailsPageStore((state) => state.build);
  const cacheInfo = useBuildDetailsPageStore((state) => state.cacheInfo);

  const queryParams = useQueryParams();
  const primaryTabParam = queryParams.get('tab');

  const {
    open: isContactSalesDialogOpen,
    onOpen: onContactSalesDialogOpen,
    onClose: onContactSalesDialogClose,
  } = useDisclosure();

  const isBuildRunning = isBuildNotFinished(build);
  const isTrialExpired = buildCacheTrial && !buildCacheTrial.isActive;
  const noInvocations = !cacheInfo || cacheInfo.invocations?.length === 0;
  const cacheCandidateSteps = cacheInfo?.cacheCandidate?.steps;

  const showCacheCandidateUi =
    !isBuildRunning &&
    cacheCandidateSteps &&
    cacheCandidateSteps.length > 0 &&
    (!cacheInfo.invocations || cacheInfo.invocations.length === 0);

  const showNoCacheConnectionAlert =
    !isBuildRunning &&
    ((buildCacheTrial && buildCacheTrial.isActive) || hasBuildCacheSubscription) &&
    cacheInfo?.anyInvocationForWorkflow &&
    !cacheInfo?.usesCache &&
    !cacheInfo?.hasBaselineInvocation;

  const cannotStartAddNewConnection =
    !canActivateBuildCacheTrial && !canViewWorkspace && !buildCacheTrial && !hasBuildCacheSubscription;

  const canStartTrial = !buildCacheTrial && !cannotStartAddNewConnection;
  const showSetupBuildCacheSteps =
    showCacheCandidateUi &&
    (!buildCacheTrial || buildCacheTrial.isActive || hasBuildCacheSubscription) &&
    build.triggeredWorkflow;
  const shouldProceed =
    !hasBuildCacheSubscription &&
    (!buildCacheTrial || (isTrialExpired && !buildCacheSubscriptionRequest && (noInvocations || isBuildRunning)));
  // The card renders ActivationAlert inside itself, so the tab-level one would duplicate it.
  const showActivationCard = !isBuildCacheTrialLoading && !showCacheCandidateUi && shouldProceed;

  useEffect(() => {
    if (showSetupBuildCacheSteps && primaryTabParam === BuildDetailsTabId.buildCache) {
      segmentTrackOnce('Set Up Build Cache Connection Configure Workflow Step Displayed', {
        app_slug: app.slug,
        build_cache_tool: BuildCacheToolIds.gradle,
        build_slug: build.slug,
        project_id: app.projectId ?? 'unsetProjectId',
        project_title: app.title,
        has_build_cache_trial: !!buildCacheTrial,
        has_build_cache_subscription: hasBuildCacheSubscription,
        is_start_caching_trial_option_available: canStartTrial,
      });
    }
  }, [
    app,
    build.slug,
    buildCacheTrial,
    canStartTrial,
    hasBuildCacheSubscription,
    primaryTabParam,
    showSetupBuildCacheSteps,
  ]);

  const renderContent = () => {
    // Trial state is an async query; hold the trial-dependent content until it resolves so we don't
    // flash the ActivationCard (no-trial branch) before the real state is known.
    if (isBuildCacheTrialLoading) {
      return null;
    }

    if (showCacheCandidateUi) {
      return (
        <Box display="flex" flexDirection="column" gap="32" maxWidth="64rem" height="fit-content">
          {cacheCandidateSteps && <OptimizableGradleSteps steps={cacheCandidateSteps} />}
          {showSetupBuildCacheSteps && <SetUpBuildCache build={build} />}
          {isTrialExpired && (
            <Box display="flex" flexDirection="column" gap="4">
              <Text textStyle="heading/h3" color="text/primary">
                Set up Build Cache
              </Text>
              <Text textStyle="body/md/regular" color="text/secondary">
                To optimize build duration of this Workflow, complete the following 2-step process.
              </Text>
            </Box>
          )}
        </Box>
      );
    }

    if (showActivationCard) {
      return (
        <ActivationCard
          sourcePageType={BuildCacheActivationSourcePageType.buildDetailsBuildCacheTab}
          projectId={app.projectId}
        />
      );
    }

    if (noInvocations || isBuildRunning) {
      return (
        <Box display="flex" flexDirection="column" gap="24">
          {build.status === BuildStatus.Error &&
            !hasBuildCacheSubscription &&
            (!buildCacheTrial || !buildCacheTrial.isActive) && (
              <BitkitAlert variant="info" messageText="You can only activate build cache from successful builds." />
            )}
          <BitkitEmptyState
            bodyText={
              <>
                {isBuildRunning
                  ? 'If you haven’t connected your project yet, set up Build Cache to reduce your build and test durations'
                  : 'Set up Build Cache for your project to reduce your build and test durations'}{' '}
                <BitkitLink
                  colorVariant="purple"
                  href="https://bitrise.io/blog/post/bitrise-build-cache-reduces-circleci-build-times#:~:text=We%20benchmarked%20open%2Dsource%20Bazel,of%20%E2%80%9CInfrastructure%20Fail%E2%80%9D%20errors."
                  isExternal
                >
                  up to 90%.
                </BitkitLink>
              </>
            }
            icon={IconBuildCache}
            headingText="Build Cache performance data would appear here"
          >
            {build.status !== BuildStatus.Error && !buildCacheSubscriptionRequest && (
              <BitkitButton
                href={buildCacheConnectionPath(accountSlug)}
                suffixIcon={IconArrowNortheast}
                size="md"
                isExternal
                variant="tertiary"
                onClick={() => {
                  segmentTrack('Add New Build Cache Connection Flow Started', {
                    project_id: app.projectId,
                    source: BuildCacheActivationSourcePageType.buildDetailsBuildCacheTab,
                    workspace_slug: accountSlug,
                  });
                }}
              >
                Set up Build Cache
              </BitkitButton>
            )}
          </BitkitEmptyState>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap="32">
        {showNoCacheConnectionAlert && (
          <BitkitAlert
            variant="warning"
            action={{
              label: 'View documentation',
              href: 'https://docs.bitrise.io/en/bitrise-build-cache/getting-started-with-the-build-cache/getting-started-with-the-build-cache.html',
            }}
            data-testid="noCacheConnectionDetectedNotification"
            titleText="No cache connection detected"
            messageText="Please check your build cache setup. Useful information can be found in the documentation."
          />
        )}
        <BuildInvocations buildSlug={build.slug} buildCacheInfo={cacheInfo} />
        <Box marginBlockStart="8" textAlign="center">
          Need help with build cache?{' '}
          <BitkitLinkButton onClick={onContactSalesDialogOpen}>Contact support</BitkitLinkButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="24" data-clarity-unmask="true">
      {!isBuildCacheTrialLoading && !showActivationCard && (
        <ActivationAlert
          sourcePageType={BuildCacheActivationSourcePageType.buildDetailsBuildCacheTab}
          trial={buildCacheTrial}
        />
      )}
      {renderContent()}
      <RequestSupportDialog isOpen={isContactSalesDialogOpen} onClose={onContactSalesDialogClose} />
    </Box>
  );
};

export default BuildCacheOfBuild;
